import React, { useEffect, useState } from 'react';
import Topbar from './components/Topbar';
import Footer from './components/Footer';
import AboutPage from './pages/AboutPage';
import Home from './pages/Home';
import CatalogStandalonePage from './pages/CatalogPage';
import fullLogo from './assets/only-fish.webp';
import SeoHead from './components/SeoHead';
import './App.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [logoReady, setLogoReady] = useState(false);
  const [locationState, setLocationState] = useState(() => ({
    pathname: window.location.pathname,
    hash: window.location.hash,
  }));
  const isAbout = locationState.pathname === '/about';
  const isCatalog = locationState.pathname === '/catalog';
  const isStandalone = isAbout || isCatalog;
  const siteUrl = 'https://frfisheries.com';

  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    const markReady = () => {
      if (cancelled) return;
      setLogoReady(true);
    };

    image.addEventListener('load', markReady);
    image.addEventListener('error', markReady);
    image.src = fullLogo;

    if (image.complete) {
      markReady();
    }

    return () => {
      cancelled = true;
      image.removeEventListener('load', markReady);
      image.removeEventListener('error', markReady);
    };
  }, []);

  useEffect(() => {
    if (!logoReady) return;
    let timeoutId: number | null = null;
    let forceId: number | null = null;
    let completed = false;
    const minDurationMs = 1600;
    const maxDurationMs = 4000;
    const start = window.performance.now();

    const complete = () => {
      if (completed) return;
      completed = true;
      if (forceId !== null) {
        window.clearTimeout(forceId);
        forceId = null;
      }
      const elapsed = window.performance.now() - start;
      const remaining = Math.max(0, minDurationMs - elapsed);
      timeoutId = window.setTimeout(() => {
        setIsLoading(false);
      }, remaining);
    };

    forceId = window.setTimeout(() => {
      setIsLoading(false);
    }, maxDurationMs);

    if (document.readyState === 'complete') {
      window.requestAnimationFrame(complete);
    } else {
      window.addEventListener('load', complete);
    }

    return () => {
      window.removeEventListener('load', complete);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (forceId !== null) {
        window.clearTimeout(forceId);
      }
    };
  }, [logoReady]);

  useEffect(() => {
    const onPopState = () => {
      setLocationState({
        pathname: window.location.pathname,
        hash: window.location.hash,
      });
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const navigate = (href: string) => {
    const url = new URL(href, window.location.origin);
    window.history.pushState({}, '', `${url.pathname}${url.hash}`);
    setLocationState({ pathname: url.pathname, hash: url.hash });
  };

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const topbar = document.querySelector<HTMLElement>('.topbar');
    let hashScrollAttempts = 0;

    const scrollToHash = () => {
      if (locationState.pathname !== '/' || !locationState.hash) return;
      const target = document.querySelector<HTMLElement>(locationState.hash);
      if (!target) {
        if (hashScrollAttempts < 3) {
          hashScrollAttempts += 1;
          window.requestAnimationFrame(scrollToHash);
        }
        return;
      }
      const offset = (topbar?.offsetHeight ?? 0) + 12;
      let scrollTarget = target;
      if (locationState.hash === '#contact') {
        const contactForm = target.querySelector<HTMLElement>('.contact-form');
        if (contactForm) {
          scrollTarget = contactForm;
        }
      }
      const targetY = scrollTarget.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: targetY,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    };

    window.requestAnimationFrame(scrollToHash);
  }, [locationState.hash, locationState.pathname]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = Array.from(document.querySelectorAll<HTMLElement>('.reveal, .scroll-reveal'));
    if (targets.length === 0) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    let observer: IntersectionObserver | null = null;
    let activated = false;

    const revealObserver = () => {
      if (activated) return;
      activated = true;
      observer = new IntersectionObserver(
        (entries, activeObserver) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              activeObserver.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.2,
          rootMargin: '0px 0px -10% 0px',
        },
      );

      targets.forEach((target) => observer?.observe(target));
    };

    const onFirstScroll = (event: Event) => {
      if (event instanceof KeyboardEvent) {
        const keys = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar'];
        if (!keys.includes(event.key)) {
          return;
        }
      }
      revealObserver();
      removeActivationListeners();
    };

    const removeActivationListeners = () => {
      window.removeEventListener('scroll', onFirstScroll);
      window.removeEventListener('wheel', onFirstScroll);
      window.removeEventListener('touchmove', onFirstScroll);
      window.removeEventListener('keydown', onFirstScroll);
    };

    if (window.scrollY > 0) {
      revealObserver();
    } else {
      window.addEventListener('scroll', onFirstScroll, { passive: true });
      window.addEventListener('wheel', onFirstScroll, { passive: true });
      window.addEventListener('touchmove', onFirstScroll, { passive: true });
      window.addEventListener('keydown', onFirstScroll);
    }

    return () => {
      removeActivationListeners();
      observer?.disconnect();
    };
  }, [locationState.pathname]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || isAbout || isCatalog) return;

    const layers = Array.from(document.querySelectorAll<HTMLElement>('.parallax-layer'));
    if (layers.length === 0) return;

    let latestScrollY = window.scrollY;
    let currentScrollY = latestScrollY;
    let rafId: number | null = null;

    const update = () => {
      const delta = latestScrollY - currentScrollY;
      currentScrollY += delta * 0.08;
      layers.forEach((layer) => {
        const speedRaw = layer.dataset.speed;
        const speed = speedRaw ? Number(speedRaw) : 0.12;
        if (!Number.isFinite(speed)) return;
        const translateY = currentScrollY * speed;
        layer.style.setProperty('--parallax-offset', `${translateY}px`);
      });

      if (Math.abs(delta) > 0.1) {
        rafId = window.requestAnimationFrame(update);
      } else {
        rafId = null;
      }
    };

    const onScroll = () => {
      latestScrollY = window.scrollY;
      if (rafId === null) {
        rafId = window.requestAnimationFrame(update);
      }
    };

    const onResize = () => {
      latestScrollY = window.scrollY;
      if (rafId === null) {
        rafId = window.requestAnimationFrame(update);
      }
    };

    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      layers.forEach((layer) => {
        layer.style.removeProperty('--parallax-offset');
      });
    };
  }, [isAbout, isCatalog]);

  if (isLoading) {
    if (!logoReady) {
      return null;
    }
    return (
      <div className="loader-screen" role="status" aria-live="polite" aria-label="Loading">
        <div className="loader-content">
          <div className="loader-card" aria-hidden="true">
            <img
              className="loader-logo"
              src={fullLogo}
              alt="FR Fisheries"
              loading="lazy"
              decoding="async"
            />
            <div className="brand-name red">FR Fisheries</div>
          </div>
          <div className="loader-slogan">Decades of freshness, delivered</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" aria-busy={isLoading}>
      <SeoHead
        title={
          isAbout
            ? 'About FR Fisheries | Heritage & Quality Seafood'
            : isCatalog
              ? 'Seafood Catalog | FR Fisheries Live Wholesale Fish'
              : 'FR Fisheries | Fresh Fish & Seafood Wholesale India'
        }
        description={
          isAbout
            ? 'Discover how FR Fisheries grew from a 1988 stall to a nationwide seafood wholesaler, led by Fairoz Basha and MD Mohammed Faizan.'
            : isCatalog
              ? 'Browse the FR Fisheries live seafood catalog: pomfret, seer fish, tuna, prawns, lobster with fast, cold-chain delivery across India.'
              : 'Live seafood catalog from 1,800+ boats and 231 hubs. Fresh pomfret, tuna, prawns, lobster with cold-chain delivery across India.'
        }
        url={`${siteUrl}${locationState.pathname}`}
        type={isAbout ? 'about' : isCatalog ? 'collection' : 'website'}
        breadcrumbs={
          isAbout
            ? [
                { name: 'Home', url: `${siteUrl}/` },
                { name: 'About', url: `${siteUrl}/about` },
              ]
            : isCatalog
              ? [
                  { name: 'Home', url: `${siteUrl}/` },
                  { name: 'Catalog', url: `${siteUrl}/catalog` },
                ]
              : undefined
        }
        faq={
          isStandalone
            ? undefined
            : [
                {
                  question: 'Do you deliver fresh fish across India?',
                  answer:
                    'Yes. FR Fisheries operates 231 coastal hubs with temperature-controlled logistics to deliver fresh seafood across major Indian metros and tier-2 cities.',
                },
                {
                  question: 'How often is your catalog updated?',
                  answer:
                    'Our live catalog refreshes every 15 minutes with landed volumes, species availability, and grades so buyers can place timely orders.',
                },
                {
                  question: 'Can restaurants request custom cuts or grading?',
                  answer:
                    'Absolutely. We support custom cuts, filleting, grading, and yield guarantees to match restaurant prep standards and reduce waste.',
                },
                {
                  question: 'What cold-chain controls do you use?',
                  answer:
                    'We monitor temperature end-to-end with IoT sensors, insulated totes, and refrigerated fleets. Any deviation triggers automated escalation.',
                },
              ]
        }
        pageSchema={[
          isAbout
            ? {
                '@type': 'AboutPage',
                name: 'About FR Fisheries',
                url: `${siteUrl}/about`,
                description:
                  'About FR Fisheries leadership, legacy, and nationwide seafood distribution network.',
              }
            : isCatalog
              ? {
                  '@type': 'CollectionPage',
                  name: 'FR Fisheries Seafood Catalog',
                  url: `${siteUrl}/catalog`,
                  description:
                    'Live seafood catalog featuring pomfret, seer fish, tuna, prawns, lobster, and more.',
                }
              : {
                  '@type': 'WebPage',
                  name: 'FR Fisheries',
                  url: `${siteUrl}/`,
                  description:
                    'FR Fisheries delivers fresh, responsibly sourced seafood and fish across India with cold-chain logistics.',
                },
        ].filter(Boolean) as Record<string, unknown>[]}
      />
      <div className="grain" aria-hidden="true" />
      {!isStandalone && (
        <>
          <div className="parallax-layer layer-one" data-speed="0.06" aria-hidden="true" />
          <div className="parallax-layer layer-two" data-speed="0.12" aria-hidden="true" />
          <div className="parallax-layer layer-three" data-speed="0.18" aria-hidden="true" />
        </>
      )}
      <Topbar onNavigate={navigate} />
      <main>
        {isAbout ? (
          <AboutPage />
        ) : isCatalog ? (
          <CatalogStandalonePage />
        ) : (
          <Home onNavigate={navigate} />
        )}
      </main>
      {!isStandalone && <Footer />}
    </div>
  );
}

export default App;
